import React from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Shield, 
  Eye, 
  Lock, 
  AlertTriangle, 
  FileCheck, 
  Database,
  Network,
  Smartphone,
  Server,
  CheckCircle,
  TrendingUp
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Cybersecurity = () => {
  const threats = [
    { name: 'Ransomware Attacks', percentage: '75%' },
    { name: 'Phishing Attempts', percentage: '83%' },
    { name: 'Data Breaches', percentage: '68%' },
    { name: 'Insider Threats', percentage: '34%' }
  ];

  const securityServices = [
    {
      icon: Eye,
      title: '24/7 Threat Monitoring',
      description: 'Continuous monitoring of your network and systems to detect and respond to threats in real-time.',
      features: [
        'Real-time threat detection',
        'Security incident response',
        'Advanced analytics',
        'Automated alerts'
      ]
    },
    {
      icon: Shield,
      title: 'Firewall Management',
      description: 'Enterprise-grade firewall configuration and management to protect your network perimeter.',
      features: [
        'Next-generation firewalls',
        'Traffic filtering',
        'Intrusion prevention',
        'VPN management'
      ]
    },
    {
      icon: FileCheck,
      title: 'Vulnerability Assessments',
      description: 'Regular security assessments to identify and address potential vulnerabilities before they can be exploited.',
      features: [
        'Penetration testing',
        'Risk assessments',
        'Compliance audits',
        'Security reporting'
      ]
    },
    {
      icon: Database,
      title: 'Data Protection',
      description: 'Comprehensive data encryption and protection strategies to safeguard your sensitive information.',
      features: [
        'Data encryption',
        'Access controls',
        'Data loss prevention',
        'Backup security'
      ]
    },
    {
      icon: Network,
      title: 'Network Security',
      description: 'Advanced network security measures including intrusion detection and prevention systems.',
      features: [
        'Network segmentation',
        'Intrusion detection',
        'Traffic analysis',
        'Anomaly detection'
      ]
    },
    {
      icon: Smartphone,
      title: 'Endpoint Protection',
      description: 'Comprehensive protection for all devices connected to your network, including mobile devices.',
      features: [
        'Anti-malware protection',
        'Device management',
        'Patch management',
        'Mobile security'
      ]
    }
  ];

  const complianceStandards = [
    'GDPR (General Data Protection Regulation)',
    'ISO 27001 Information Security',
    'Cyber Essentials Certification',
    'PCI DSS Payment Security',
    'SOC 2 Compliance',
    'NIST Cybersecurity Framework'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-security via-primary to-primary-dark text-primary-foreground py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Advanced Cybersecurity Solutions
            </h1>
            <p className="text-xl lg:text-2xl text-primary-foreground/90 mb-8 leading-relaxed">
              Protect your business from evolving cyber threats with our comprehensive 
              cybersecurity services and 24/7 security monitoring.
            </p>
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Contact Our Security Experts</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Threat Statistics */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              The Cybersecurity Landscape
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Cyber threats are growing in sophistication and frequency. Protect your business 
              with our proactive cybersecurity approach.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {threats.map((threat, index) => (
              <Card key={index} className="text-center border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="text-3xl font-bold text-destructive mb-2">{threat.percentage}</div>
                  <div className="text-sm text-muted-foreground">of businesses face</div>
                  <div className="text-foreground font-medium mt-2">{threat.name}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Card className="bg-destructive/10 border-destructive/20 max-w-2xl mx-auto">
              <CardContent className="p-8">
                <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Don't Become a Statistic
                </h3>
                <p className="text-muted-foreground">
                  The average cost of a data breach is £3.2 million. Invest in cybersecurity 
                  before it's too late.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Security Services */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Comprehensive Security Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our multi-layered approach to cybersecurity provides complete protection 
              for your business assets and data.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityServices.map((service, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow h-full">
                <CardHeader className="text-center pb-4">
                  <div className="bg-security/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <service.icon className="h-8 w-8 text-security" />
                  </div>
                  <CardTitle className="text-xl text-foreground">{service.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Process */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Security Approach
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              We follow a systematic approach to cybersecurity that ensures comprehensive 
              protection and rapid response to threats.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Assessment', description: 'Comprehensive security audit and risk assessment' },
              { step: '02', title: 'Planning', description: 'Custom security strategy and implementation plan' },
              { step: '03', title: 'Implementation', description: 'Deploy advanced security measures and monitoring' },
              { step: '04', title: 'Monitoring', description: '24/7 threat monitoring and incident response' }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="bg-primary text-primary-foreground w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {phase.step}
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{phase.title}</h3>
                <p className="text-muted-foreground">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-20 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Compliance & Standards
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Stay compliant with industry regulations and standards. Our cybersecurity 
                solutions are designed to meet and exceed regulatory requirements.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {complianceStandards.map((standard, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                    <span className="text-foreground text-sm">{standard}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-security to-primary-dark text-primary-foreground border-none shadow-xl">
                <CardContent className="p-8">
                  <TrendingUp className="h-12 w-12 mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Security ROI</h3>
                  <p className="text-primary-foreground/90 mb-6">
                    For every £1 invested in cybersecurity, businesses save an average of £5 
                    in prevented breach costs and downtime.
                  </p>
                  <Button asChild variant="secondary" size="lg" className="w-full">
                    <Link to="/contact">Calculate Your Security ROI</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-r from-security to-primary text-primary-foreground border-none">
            <CardContent className="p-12 text-center">
              <Shield className="h-16 w-16 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Secure Your Business Today
              </h2>
              <p className="text-xl text-primary-foreground/90 mb-8 max-w-3xl mx-auto">
                Don't wait for a cyber attack to invest in security. Contact our cybersecurity 
                experts for a free security assessment and protect your business now.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild variant="secondary" size="lg">
                  <Link to="/contact">Get Free Security Assessment</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="text-primary-foreground border-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  <Link to="/services">View All Services</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Cybersecurity;